import './AccountSelector.scss';

import * as React from 'react';

import { HealAPI } from '../../healApi';

import { IBasePickerSuggestionsProps, assign, Label, NormalPeoplePicker, IPersonaProps, PersonaSize, Persona, Link, Spinner, IconButton } from '@fluentui/react';
import { ROUTES } from '../../config/routes';
import { Account } from '../../api/accounts.types';

const limitedSearchAdditionalProps: IBasePickerSuggestionsProps = {
  resultsMaximumNumber: 10,
  className: 'account-selector-suggestion-item',
};

const suggestionProps: IBasePickerSuggestionsProps = {
  suggestionsHeaderText: 'Suggested Accounts',
  noResultsFoundText: 'No results found',
};

const limitedSearchSuggestionProps: IBasePickerSuggestionsProps = assign(limitedSearchAdditionalProps, suggestionProps);

export interface AccountSelectorProps {
  label?: string;
  userAccountId?: string;
  readonly?: boolean;
  onChange: (account: Account) => void;
  tid?: string;
}

export interface AccountSelectorState {
  selectedAccount?: Account;
  editAccount?: boolean;
  isFetching?: boolean;
}

export class AccountSelector extends React.Component<AccountSelectorProps, AccountSelectorState> {
  constructor(props: AccountSelectorProps) {
    super(props);
    this.state = {};
  }

  public componentDidMount() {
    if (this.props.userAccountId) {
      this.getAccountDetails(this.props.userAccountId);
    }
  }

  public componentDidUpdate(prevProps: AccountSelectorProps) {
    if (
      this.props.userAccountId !== prevProps.userAccountId &&
      this.props.userAccountId &&
      (!this.state.selectedAccount || (this.state.selectedAccount && this.props.userAccountId !== this.state.selectedAccount.userAccountId))
    ) {
      this.getAccountDetails(this.props.userAccountId);
    }
  }

  public render(): JSX.Element {
    return (
      <div data-tid={this.props.tid}>
        {this.props.label !== '' && <Label>{this.props.label || 'Account (search for an existing account)'}</Label>}
        <div style={{ display: 'inline-flex' }}>
          {this.state.isFetching ? <Spinner style={{ alignItems: 'flex-start', marginTop: 6, marginBottom: 6 }} /> : this.renderAccountSelector()}
        </div>
        {!this.props.readonly && (
          <IconButton
            iconProps={{ iconName: `${this.state.editAccount ? 'RevToggleKey' : 'Edit'}` }}
            style={{
              marginLeft: this.state.editAccount ? 6 : 2,
            }}
            onClick={() => this.setState({ editAccount: !this.state.editAccount })}
          />
        )}
      </div>
    );
  }

  private renderAccountSelector(): JSX.Element | null {
    if (!this.state.editAccount) {
      const onRenderPrimaryText = () => {
        return (
          <Link target="_blank" href={`${ROUTES.accounts}/${this.state.selectedAccount!.userAccountId}`}>
            {this.state.selectedAccount!.fullName}
          </Link>
        );
      };
      return (
        <div style={{ marginTop: 4, marginBottom: 4 }}>
          {this.state.selectedAccount && (
            <Persona
              size={PersonaSize.size24}
              text={this.state.selectedAccount.fullName}
              onRenderPrimaryText={onRenderPrimaryText}
              imageUrl={this.state.selectedAccount.avatarUrl}
            />
          )}
        </div>
      );
    } else {
      return (
        <NormalPeoplePicker
          onResolveSuggestions={this._onFilterChanged}
          key={'controlled'}
          selectedItems={this.formatAccountDataForPicker([this.state.selectedAccount])}
          pickerSuggestionsProps={limitedSearchSuggestionProps}
          onChange={this._onItemsChange}
          resolveDelay={300}
          itemLimit={2}
        />
      );
    }
  }

  private _onFilterChanged = (filterText: string): IPersonaProps[] | Promise<IPersonaProps[]> => {
    if (filterText) {
      return HealAPI.searchAccount(filterText).then((response) => {
        if (!response.ok || !response.data) {
          return [];
        } else {
          return this.formatAccountDataForPicker(response.data);
        }
      });
    }
    return [];
  };

  private _onItemsChange = (items: any): void => {
    if (items.length > 0) {
      this.getAccountDetails(items[0].userAccountId);
    } else {
      this.setState({ selectedAccount: undefined });
    }
  };

  private formatAccountDataForPicker(accounts: any[]): IPersonaProps[] {
    if (accounts[0]) {
      return accounts
        .filter((a) => a.personType === 'USER')
        .map((a) => {
          return {
            text: a.fullName,
            secondaryText: a.email,
            imageUrl: a.avatarUrl,
            userAccountId: a.userAccountId,
          };
        });
    } else {
      return [];
    }
  }

  private getAccountDetails(userAccountId: string): void {
    this.setState({ isFetching: true });
    HealAPI.fetchAccountInfo(userAccountId).then((response) => {
      if (response.ok && response.data) {
        const selectedAccount = response.data;
        if (this.props.onChange) {
          this.props.onChange(selectedAccount);
        }
        this.setState({
          selectedAccount: response.data,
          editAccount: false,
          isFetching: false,
        });
      } else {
        this.setState({ selectedAccount: undefined, isFetching: false });
      }
    });
  }
}
